/**
 * Category utility functions for handling composite keys
 *
 * Composite key format: "system-{id}" or "custom-{id}"
 * This ensures unique identification when system and custom categories have the same numeric ID
 */

export type CategoryKey = string; // Format: "system-{id}" or "custom-{id}"

/**
 * Creates a composite key from a category ID and is_custom flag
 * @param id - The numeric category ID
 * @param isCustom - Whether this is a custom category
 * @returns Composite key in format "system-{id}" or "custom-{id}"
 */
export const createCategoryKey = (id: number, isCustom: boolean): CategoryKey => {
  return isCustom ? `custom-${id}` : `system-${id}`;
};

/**
 * Parses a composite key into its component parts
 * @param key - The composite key to parse
 * @returns Object with id and isCustom properties
 */
export const parseCategoryKey = (key: CategoryKey): { id: number; isCustom: boolean } => {
  // Handle legacy format where key might just be a number string (e.g., "12")
  if (!key.includes('-')) {
    return {
      id: parseInt(key, 10),
      isCustom: false,
    };
  }
  const [type, idStr] = key.split('-');
  return {
    id: parseInt(idStr, 10),
    isCustom: type === 'custom',
  };
};

/**
 * Extracts the numeric ID from a composite key
 * @param key - The composite key
 * @returns The numeric ID
 */
export const extractNumericId = (key: CategoryKey): number => {
  // Handle legacy format where key might just be a number string (e.g., "12")
  if (!key.includes('-')) {
    return parseInt(key, 10);
  }
  const parts = key.split('-');
  return parseInt(parts[1], 10);
};

/**
 * Converts an array of composite keys to numeric IDs
 * @param keys - Array of composite keys
 * @returns Array of numeric IDs
 * @deprecated Use categoryKeysToApiFormat() for API submission
 */
export const categoryKeysToIds = (keys: CategoryKey[]): number[] => {
  return keys.map(extractNumericId);
};

/**
 * Converts composite keys to API format
 * - Custom categories: keeps as "custom-{id}" string
 * - System categories: converts "system-{id}" to numeric ID
 *
 * This is required because the backend needs composite keys for custom categories
 * to distinguish them from system categories with the same numeric ID.
 *
 * @param keys - Array of composite keys
 * @returns Array of strings (custom) or numbers (system) for API submission
 * @example
 * categoryKeysToApiFormat(["system-3"]) → [3]
 * categoryKeysToApiFormat(["custom-3"]) → ["custom-3"]
 */
export const categoryKeysToApiFormat = (keys: CategoryKey[]): (string | number)[] => {
  return keys.map((key) => {
    if (key.startsWith('custom-')) {
      return key; // Keep "custom-3" as string for API
    }
    // Convert "system-3" to number 3 for API
    return extractNumericId(key);
  });
};

/**
 * Converts numeric IDs + category list to composite keys
 * Used when loading existing document data from API
 * @param ids - Array of numeric category IDs
 * @param categoryList - List of all available categories
 * @returns Array of composite keys
 */
export const categoryIdsToKeys = (
  ids: number[],
  categoryList: Array<{ id: number; is_custom?: boolean }>,
): CategoryKey[] => {
  return ids.map((id) => {
    const category = categoryList.find((cat) => cat.id === id);
    return createCategoryKey(id, category?.is_custom || false);
  });
};

/**
 * Select a single category (single-select mode)
 * @param key - The composite key of the category to select
 * @returns Array with only the selected key
 */
export const selectCategoryKey = (key: CategoryKey): CategoryKey[] => {
  return [key];
};

/**
 * Toggle selection of a category (multi-select mode)
 * Not currently used but available for future enhancement
 * @param key - The composite key to toggle
 * @param selectedKeys - Current array of selected keys
 * @returns Updated array of selected keys
 */
export const toggleCategoryKey = (key: CategoryKey, selectedKeys: CategoryKey[]): CategoryKey[] => {
  const index = selectedKeys.indexOf(key);
  if (index !== -1) {
    const newKeys = [...selectedKeys];
    newKeys.splice(index, 1);
    return newKeys;
  }
  return [...selectedKeys, key];
};
